import React, { useCallback, useMemo } from 'react';
import { useLingui } from '@lingui/react/macro';
import { ph } from '@lingui/core/macro';
import { useNavigate } from 'react-router-dom';

import { ANNOUNCED_TEAM_STATUS_LABELS } from 'modules/teams/domain/constants/application-status-labels';
import { ApplicationStatus } from 'modules/teams/domain/enums/application-status';
import { AnnouncedTeam } from 'modules/teams/domain/interfaces/announced-team';
import { TEAM_DEFAULT_LOGO } from 'shared/constants/links';
import { UDEntityRow } from 'ud-ui/components';
import UDButton from 'ud-ui/components/button';

interface TeamApplicationRowProps {
  team: AnnouncedTeam;
  /** Порядковый номер строки, уже готовый к показу. */
  index: number;
  /**
   * Лимит заявки турнира (`applicationMaxPlayers`) — знаменатель «заявлено N из M».
   * Считает его экран: строка не знает `championshipId`, а лимит у всех строк один.
   */
  maxPlayers: number;
}

/**
 * Строка списка заявок v2: логотип, название, статус и разбор заявок команды.
 *
 * Главное отличие от карточки старого флоу (`championship-teams/championship-team-card.tsx`) —
 * здесь нет кнопок «Принять»/«Отклонить». Решение по команде принимается внутри её заявки,
 * где видно, кого именно заявляют; в списке остаётся только переход.
 */
const TeamApplicationRow = ({ team, index, maxPlayers }: TeamApplicationRowProps) => {
  const { t } = useLingui();
  const navigate = useNavigate();

  const open = useCallback(() => navigate(String(team.id)), [navigate, team.id]);

  const subtitle = useMemo(() => {
    const parts = [t(ANNOUNCED_TEAM_STATUS_LABELS[team.status])];

    if (team.applications.length === 0) {
      // Команда без единой заявки приходит из `championship { teams }` (фаза 7): состав у неё
      // может быть, просто его ещё не заявляли — это не то же, что команда без игроков вовсе.
      parts.push(
        team.membersCount ? t`игроки ещё не заявлены, в составе ${ph({ members: team.membersCount })}` : t`в команде пока нет игроков`,
      );

      return parts.join(' · ');
    }

    const countBy = (status: ApplicationStatus) => team.applications.filter((application) => application.status === status).length;

    const accepted = countBy(ApplicationStatus.ACCEPTED);
    const declined = countBy(ApplicationStatus.DECLINED);
    const pending = countBy(ApplicationStatus.PENDING);

    // Знаменатель — лимит турнира, а не число заявок: «заявлено 3 из 87» ничего не говорит
    // о том, сколько игроков вообще можно заявить.
    parts.push(t`заявлено ${accepted} из ${ph({ limit: maxPlayers })}`);

    // Нули не печатаем: у команды без отклонённых хвост «отклонено 0» только шумит.
    if (declined > 0) parts.push(t`отклонено ${declined}`);
    if (pending > 0) parts.push(t`ждут решения ${pending}`);
    if (team.membersCount) {
      parts.push(t`рассмотрено ${ph({ reviewed: accepted + declined })} из ${ph({ members: team.membersCount })} игроков команды`);
    }

    return parts.join(' · ');
  }, [t, team.applications, team.status, team.membersCount, maxPlayers]);

  const title = team.shortName ? `${team.name} (${team.shortName})` : team.name;

  return (
    <UDEntityRow
      data-testid="team-application-row"
      index={index}
      avatar={team.logo?.url || TEAM_DEFAULT_LOGO}
      title={title}
      subtitle={subtitle}
      onClick={open}
      actions={
        <UDButton variant="secondary" onClick={open}>
          {t`Открыть`}
        </UDButton>
      }
    />
  );
};

export default React.memo(TeamApplicationRow);
